import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import Modal from "react-modal";
import ky from "ky-universal";
import cx from "classnames";

import { chocomemberHost, chocotvWeb } from "../../../config/api";
import {
  AUTH_TYPE_SIGNUP,
  OAUTH_PROVIDER_DISPLAY_NAME,
} from "../../../constants/oauth";
import { AUTH_MODAL_CLOSED } from "../../reducers/ui/authModal";
import {
  showCampaignSucceedModal,
  showCampaignWayoutModal,
} from "../../reducers/ui/campaignModal";
import { CAMPAIGN_EVENT6 } from "../../reducers/ui/campaignSource";
import {
  CAMPAIGN_STEP_POLICY,
  CAMPAIGN_STEP_SUCCEED,
  setCampaignStep,
} from "../../reducers/ui/campaignStep";
import { isCampaignAttendedSelector } from "../../selector/campaign";
import {
  policiesSelector,
  isPolicyModalOpenSelector,
  profileSelector,
} from "../../selector/user";
import { isMobileSelector } from "../../selector/device";
import { logout, refreshProfile, updatePolicies } from "../../actions/user";
import authUtils from "../../utils/auth";
import sendImpressionPlacementEvent from "../../utils/tracking/sendImpressionPlacementEvent";
import sendLegacyEvent from "../../utils/tracking/sendLegacyEvent";
import Checkbox from "../Checkbox";
import TextInput, { InvalidFeedBack } from "../TextInput";
import { returnCampaignWording } from '../../utils/campaign/returnCampaignWording';
import { ga4RegisterSuccess } from "../../utils/ga4";

const campaignWording = returnCampaignWording();

function PolicyModal({
  profile,
  policies,
  isPolicyModalOpen,
  authModalState,
  refreshProfile,
  updatePolicies,
  logout,
  showCampaignSucceedModal,
  showCampaignWayoutModal,
  setCampaignStep,
  isCampaignFlow,
  isMobile,
}) {
  const formRef = useRef(null);
  const [isFormValid, setFormValidation] = useState(false);

  const location = useLocation();
  const providerDisplayName = OAUTH_PROVIDER_DISPLAY_NAME[location.state?.provider] ?? '';
  const isSignupFlow = location.state?.authType === AUTH_TYPE_SIGNUP;
  const isModalOpen = authModalState === AUTH_MODAL_CLOSED && isPolicyModalOpen;

  useEffect(() => {
    if (!isModalOpen) return;

    if (isCampaignFlow) {
      const event6 = window?.localStorage?.getItem(CAMPAIGN_EVENT6);
      setCampaignStep(CAMPAIGN_STEP_POLICY);
      sendLegacyEvent({
        api: "user_preference",
        event5: "flow_4",
        event6,
        event10: "member_growing_imp",
        isMobile,
        profile,
      });
    } else {
      sendImpressionPlacementEvent({
        placement_title: 'user_register_form',
        type: isSignupFlow ? 'user_register' : 'user_login',
      });
    }
  }, [isSignupFlow, isModalOpen, isCampaignFlow, isMobile, profile]);

  function onFormChange(event) {
    setFormValidation(event.target.form.checkValidity());
  }

  function onFormSubmit(event) {
    event.preventDefault();

    if (isCampaignFlow) {
      const event6 = window?.localStorage?.getItem(CAMPAIGN_EVENT6);
      sendLegacyEvent({
        api: "user_preference",
        event5: "flow_4",
        event6,
        event10: "member_growing_click",
        isMobile,
        profile,
      });
      showCampaignSucceedModal();
      sendLegacyEvent({
        api: "user_preference",
        event5: "flow_5",
        event6,
        event10: "member_growing_imp",
        isMobile,
        profile,
      });
      setCampaignStep(CAMPAIGN_STEP_SUCCEED);
    }

    const policies = Array.from(event.target.querySelectorAll('[data-policy]')).map((field) => {
      return {
        id: field.dataset.policy,
        accept: field.checked,
      };
    });

    updatePolicies(policies);

    if (isSignupFlow) {
      ky.put(`${chocomemberHost}/account/profile`, {
        json: {
          appId: chocotvWeb.chocomemberAppId,
          name: event.target.querySelector('[data-name]')?.value,
          email: event.target.querySelector('[data-email]')?.value,
        },
        headers: {
          Authorization: authUtils.getAccessToken(),
        },
      })
        .then(() => {
          const providerName = location.state?.provider;
          sendLegacyEvent({
            api: "user_preference",
            event10: "user_register_success",
            isMobile,
            profile,
          });
          ga4RegisterSuccess(profile, providerName.toLowerCase());
          window.dataLayer.push({
            event: 'REGISTER_SUCCESS',
            registration_method: providerName.toLowerCase(),
          });
          refreshProfile();
        })
        .catch((err) => '');
    }
  }

  return (
    <Modal
      isOpen={isModalOpen}
      className={{
        base: 'AuthModal-content',
        afterOpen: 'AuthModal-content--after-open',
        beforeClose: 'AuthModal-content--before-close',
      }}
      overlayClassName={{
        base: 'AuthModal-overlay',
        afterOpen: 'AuthModal-overlay--after-open',
        beforeClose: 'AuthModal-overlay--before-close',
      }}
    >
      <button
        type="button"
        className="AuthModal-floatButton AuthModal-floatButton--text"
        onClick={() => {
          if (isCampaignFlow) {
            showCampaignWayoutModal();
          } else {
            logout();
          }
        }}
      >
        登出
      </button>

      <div className={cx("AuthModal-main", isCampaignFlow ? 'py-12' : '')}>
        {!isCampaignFlow && (
          <div className="AuthModal-header">{isSignupFlow && '註冊 LINE TV'}</div>
        )}

        <form ref={formRef} onChange={onFormChange} onSubmit={onFormSubmit} className="mt-5" style={{ height: isCampaignFlow && '100%', display: isCampaignFlow && 'flex', flexDirection: isCampaignFlow && 'column' }}>
          {isSignupFlow ? (
            <>
              <p className="text-20 font-700">
                {!isCampaignFlow ? (
                  <>
                    您使用「{providerDisplayName}」註冊
                    <br />
                    只差一步即可加入我們！
                  </>
                ) : (
                  <> {`只差一步，就能獲得LINE POINTS ${campaignWording?.point1}點！`}</>
                )}
              </p>
              <div className="mt-5">
                <label htmlFor="name" className="block font-700 mb-2">
                  會員暱稱
                  <span className="text-brand-red-400">*</span>
                </label>
                <TextInput type="text" id="name" autoComplete="name" defaultValue={profile?.name} required data-name placeholder="輸入您的暱稱" />
                <InvalidFeedBack style={{ marginTop: isCampaignFlow && '0.25rem', marginBottom: isCampaignFlow && '-1.25rem' }}>請輸入暱稱</InvalidFeedBack>
              </div>
              <div className={cx(isCampaignFlow ? 'mt-6 ': 'mt-4')}>
                <label htmlFor="email" className="block font-700 mb-2">
                  聯絡信箱
                  <span className="text-brand-red-400">*</span>
                </label>
                <TextInput
                  type="email"
                  id="email"
                  autoComplete="email"
                  defaultValue={profile?.email}
                  pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                  required
                  data-email
                  placeholder="輸入您的連絡信箱"
                />
                <InvalidFeedBack style={{ marginTop: isCampaignFlow && '0.25rem', marginBottom: isCampaignFlow && '-1.25rem' }}>信箱格式錯誤</InvalidFeedBack>
              </div>
            </>
          ) : (
            <>
              <p className="text-20 font-700">條款更新</p>
              <p className="text-13 text-brand-grey-800 mt-3">
                親愛的用戶您好，巧克科技新媒體股份有限公司自2018年10月23日起成為LINE集團的一員。原CHOCO
                TV自2018年11月20日起，改以LINE TV之品牌名稱對外營運，因此我們更新了原有條款。
              </p>
            </>
          )}

          <fieldset className="mt-8">
            {policies.map((policy) => (
              <Checkbox
                key={policy.id}
                className="mt-2"
                defaultChecked={policy.accept}
                required={policy.required}
                data-policy={policy.id}
              >
                我同意 LINE TV 之
                <a href={policy.link} target="_blank" rel="noopener">
                  {policy.title}
                </a>
              </Checkbox>
            ))}
          </fieldset>

          <button type="submit" className="Btn Btn--green w-full mt-5" style={{ marginTop: isCampaignFlow && 'auto' }} disabled={!isFormValid}>
            {!isCampaignFlow ? '確認' : '送出'}
          </button>
        </form>
      </div>
    </Modal>
  );
}

function mapStateToProps(state) {
  return {
    profile: profileSelector(state),
    policies: policiesSelector(state),
    isPolicyModalOpen: isPolicyModalOpenSelector(state),
    authModalState: state.ui.authModal,
    isCampaignFlow: isCampaignAttendedSelector(state),
    isMobile: isMobileSelector(state),
  };
}

const mapDispatchToProps = {
  refreshProfile,
  updatePolicies,
  logout,
  showCampaignSucceedModal,
  showCampaignWayoutModal,
  setCampaignStep,
};

export default connect(mapStateToProps, mapDispatchToProps)(PolicyModal);
